import React, { useState, useEffect } from 'react';
import QuickCustomerForm from '../quote/QuickCustomerForm';
import QuickVehicleForm from '../quote/QuickVehicleForm';

const ContractForm = ({ contract, customers, projects, quotes, onSubmit, onCancel, loading, onCreateCustomer, onCreateVehicle }) => {
    const [formData, setFormData] = useState({
        title: '',
        customer_id: '',
        project_id: '',
        quote_id: '',
        status: 'draft',
        content: '',
        signed_at: '',
    });

    const [errors, setErrors] = useState({});
    const [availableProjects, setAvailableProjects] = useState([]);
    const [availableQuotes, setAvailableQuotes] = useState([]);

    // Quick forms state
    const [showQuickCustomerForm, setShowQuickCustomerForm] = useState(false);
    const [creatingCustomer, setCreatingCustomer] = useState(false);
    const [showQuickVehicleForm, setShowQuickVehicleForm] = useState(false);
    const [creatingVehicle, setCreatingVehicle] = useState(false);

    const statuses = [
        { value: 'draft', label: 'Brouillon' },
        { value: 'sent', label: 'Envoyé' },
        { value: 'signed', label: 'Signé' },
        { value: 'rejected', label: 'Refusé' }
    ];

    /* Contract Variables */
    const variables = [
        { label: 'Client', tag: '{{customer_name}}' },
        { label: 'Entreprise', tag: '{{customer_company}}' },
        { label: 'Projet', tag: '{{project_name}}' },
        { label: 'Code Projet', tag: '{{project_code}}' },
        { label: 'Devis #', tag: '{{quote_number}}' },
        { label: 'Total TTC', tag: '{{total_ttc}}' },
        { label: 'Date Signature', tag: '{{signed_at}}' }
    ];

    const [viewMode, setViewMode] = useState('editor'); // editor or preview

    const insertVariable = (tag) => {
        const textarea = document.getElementById('contract-content-editor');
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = formData.content;
        const before = text.substring(0, start);
        const after = text.substring(end, text.length);

        const newContent = before + tag + after;
        setFormData(prev => ({ ...prev, content: newContent }));

        // Refocus after state update
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start + tag.length, start + tag.length);
        }, 0);
    };

    /* Default template if content is empty */
    const defaultContent = `
<div style="font-family: 'Inter', sans-serif; line-height: 1.7; color: #1e293b; max-width: 800px; margin: auto;">
  <div style="text-align: center; margin-bottom: 40px;">
    <h1 style="color: #4f46e5; margin-bottom: 5px; font-size: 28px;">CONTRAT DE PRESTATION DE SERVICES</h1>
    <p style="color: #64748b; font-size: 14px;">Référence : {{quote_number}}</p>
  </div>
  
  <section style="margin-bottom: 25px;">
    <h2 style="color: #0f172a; border-bottom: 2px solid #e2e8f0; padding-bottom: 8px; font-size: 18px;">1. LES PARTIES</h2>
    <p>Le présent contrat est conclu entre :</p>
    <p><strong>LA SOCIÉTÉ :</strong> INFOCOLAB, dont le siège social est situé à [Adresse], ci-après désignée "Le Prestataire".</p>
    <p><strong>LE CLIENT :</strong> M./Mme {{customer_name}}, {{customer_company}}, ci-après désigné "Le Client".</p>
  </section>

  <section style="margin-bottom: 25px;">
    <h2 style="color: #0f172a; border-bottom: 2px solid #e2e8f0; padding-bottom: 8px; font-size: 18px;">2. OBJET DU CONTRAT</h2>
    <p>Le présent contrat a pour objet l'exécution du projet : <strong>{{project_name}}</strong> ({{project_code}}).</p>
    <p>Le détail des prestations est défini dans le devis {{quote_number}} pour un montant total de {{total_ttc}}€.</p>
  </section>

  <section style="margin-bottom: 25px;">
    <h2 style="color: #0f172a; border-bottom: 2px solid #e2e8f0; padding-bottom: 8px; font-size: 18px;">3. ENGAGEMENTS</h2>
    <p>Le Prestataire s'engage à mettre en œuvre tous les moyens nécessaires pour la bonne exécution des prestations décrites...</p>
  </section>

  <div style="margin-top: 60px; padding: 20px; background: #f8fafc; border-radius: 12px; border: 1px dashed #cbd5e1;">
    <div style="display:flex; justify-content:space-between;">
      <div style="width: 45%;">
        <p style="font-weight: 700; margin-bottom: 40px;">Pour INFOCOLAB :</p>
        <p style="font-size: 12px; color: #94a3b8;">Bon pour accord</p>
      </div>
      <div style="width: 45%;">
        <p style="font-weight: 700; margin-bottom: 40px;">Pour LE CLIENT :</p>
        <p style="font-size: 12px; color: #94a3b8;">Signature électronique certifiée</p>
        <p style="font-size: 12px; font-style: italic;">Signé le : {{signed_at}}</p>
      </div>
    </div>
  </div>
</div>`;

    useEffect(() => {
        if (contract) {
            setFormData({
                title: contract.title || '',
                customer_id: contract.customer || contract.expand?.customer?.id || '',
                project_id: contract.project || '',
                quote_id: contract.quote || '',
                status: contract.status || 'draft',
                content: contract.content || defaultContent,
                signed_at: contract.signed_at || ''
            });

            // Init available resources based on linked customer
            const cid = contract.customer || contract.expand?.customer?.id;
            if (cid) updateDependencies(cid);
        } else {
            setFormData(prev => ({ ...prev, content: defaultContent }));
        }
    }, [contract]);

    const updateDependencies = (customerId) => {
        // Filter projects
        const custProjects = projects.filter(p => (p.client || p.customer_id || p.customer) === customerId);
        setAvailableProjects(custProjects);

        // Filter quotes
        const custQuotes = quotes.filter(q => q.customer_id === customerId);
        setAvailableQuotes(custQuotes);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        if (name === 'customer_id') {
            updateDependencies(value);
            setFormData(prev => ({ ...prev, project_id: '', quote_id: '' }));
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.customer_id) { setErrors({ customer_id: 'Veuillez sélectionner un client' }); return; }
        if (!formData.title) { setErrors({ title: 'Le titre est obligatoire' }); return; }

        // Prepare payload
        // Map internal naming to Pocketbase schema
        const payload = {
            title: formData.title,
            customer: formData.customer_id, // Important: schema expects 'customer'
            project: formData.project_id || null,
            quote: formData.quote_id || null,
            status: formData.status,
            content: formData.content,
            // signed_at is usually automatic or handled separately, but we can send it if editing
        };

        onSubmit(payload);
    };

    const handleQuickCustomerSubmit = async (data) => {
        setCreatingCustomer(true);
        try {
            const res = await onCreateCustomer(data);
            setFormData(prev => ({ ...prev, customer_id: res.id }));
            updateDependencies(res.id);
            setShowQuickCustomerForm(false);
        } finally { setCreatingCustomer(false); }
    };

    const handleQuickVehicleSubmit = async (data) => {
        setCreatingVehicle(true);
        try {
            const res = await onCreateVehicle({
                ...data,
                client: formData.customer_id
            });
            setFormData(prev => ({ ...prev, project_id: res.id }));
            setAvailableProjects(prev => [...prev, res]);
            setShowQuickVehicleForm(false);
        } finally { setCreatingVehicle(false); }
    };

    return (
        <div className="quote-form-overlay">
            <div className="quote-form-container">
                <div className="quote-form-header">
                    <h2>{contract ? 'Modifier le contrat' : 'Nouveau contrat'}</h2>
                    <div style={{ display: 'flex', gap: 10 }}>
                        <div className="view-toggle" style={{ background: 'var(--admin-panel-2)', borderRadius: 8, padding: 4, display: 'flex', border: '1px solid var(--admin-border)' }}>
                            <button type="button" onClick={() => setViewMode('editor')} style={{ padding: '6px 12px', borderRadius: 6, border: 'none', background: viewMode === 'editor' ? 'var(--admin-accent)' : 'transparent', color: viewMode === 'editor' ? 'white' : 'var(--admin-text)', cursor: 'pointer' }}>Source</button>
                            <button type="button" onClick={() => setViewMode('preview')} style={{ padding: '6px 12px', borderRadius: 6, border: 'none', background: viewMode === 'preview' ? 'var(--admin-accent)' : 'transparent', color: viewMode === 'preview' ? 'white' : 'var(--admin-text)', cursor: 'pointer' }}>Aperçu</button>
                        </div>
                        <button className="close-btn" onClick={onCancel}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
                        </button>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="quote-form">
                    <div className="form-section">
                        <h3>Informations</h3>
                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="title">Titre du contrat *</label>
                                <input type="text" id="title" name="title" value={formData.title} onChange={handleChange} placeholder="Ex: Contrat de maintenance 2024" className={errors.title ? 'error' : ''} />
                                {errors.title && <span className="error-text">{errors.title}</span>}
                            </div>
                            <div className="form-group">
                                <label htmlFor="status">Statut</label>
                                <select id="status" name="status" value={formData.status} onChange={handleChange}>
                                    {statuses.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                </select>
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="customer_id">Client *</label>
                                <div className="select-with-add">
                                    <select id="customer_id" name="customer_id" value={formData.customer_id} onChange={handleChange} className={errors.customer_id ? 'error' : ''}>
                                        <option value="">Sélectionner...</option>
                                        {customers.map(c => <option key={c.id} value={c.id}>{c.name} {c.company && `(${c.company})`}</option>)}
                                    </select>
                                    <button type="button" className="add-btn" onClick={() => setShowQuickCustomerForm(true)} title="Nouveau client">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" /></svg>
                                    </button>
                                </div>
                                {errors.customer_id && <span className="error-text">{errors.customer_id}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="project_id">Projet</label>
                                <div className="select-with-add">
                                    <select id="project_id" name="project_id" value={formData.project_id} onChange={handleChange} disabled={!formData.customer_id}>
                                        <option value="">Sélectionner...</option>
                                        {availableProjects.map(p => <option key={p.id} value={p.id}>{p.name} {p.internal_code && `(${p.internal_code})`}</option>)}
                                    </select>
                                    <button type="button" className="add-btn" onClick={() => setShowQuickVehicleForm(true)} disabled={!formData.customer_id} title="Nouveau projet">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" /></svg>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="quote_id">Devis lié (optionnel)</label>
                                <select id="quote_id" name="quote_id" value={formData.quote_id} onChange={handleChange} disabled={!formData.customer_id}>
                                    <option value="">Aucun</option>
                                    {availableQuotes.map(q => <option key={q.id} value={q.id}>{q.quote_number} - {(q.total || 0).toFixed(2)}€</option>)}
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="form-section" style={{ borderBottom: 'none' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                            <h3 style={{ margin: 0 }}>Corps du contrat</h3>
                            <div className="tags-container" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                {variables.map(v => (
                                    <button key={v.tag} type="button" onClick={() => insertVariable(v.tag)} style={{ fontSize: '11px', padding: '4px 8px', borderRadius: 4, border: '1px solid var(--admin-border)', background: 'var(--admin-panel-2)', color: 'var(--admin-text)', cursor: 'pointer' }}>
                                        + {v.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {viewMode === 'editor' ? (
                            <div className="form-group">
                                <textarea
                                    id="contract-content-editor"
                                    value={formData.content}
                                    onChange={handleChange}
                                    name="content"
                                    rows={18}
                                    style={{ fontFamily: 'monospace', fontSize: '13px', background: '#111', color: '#a5d6ff', lineHeight: 1.5, border: '1px solid #30363d' }}
                                    placeholder="Contenu HTML du contrat..."
                                />
                            </div>
                        ) : (
                            <div className="html-preview" style={{ background: 'white', color: '#333', padding: '40px', borderRadius: 8, border: '1px solid #ddd', minHeight: '400px', overflowY: 'auto' }}>
                                <div dangerouslySetInnerHTML={{ __html: formData.content }} />
                            </div>
                        )}
                        <p className="info-text">💡 Utilisez l'éditeur pour structurer votre contrat avec du HTML et injecter les variables dynamiques.</p>
                    </div>

                    <div className="form-actions">
                        <button type="button" onClick={onCancel} className="btn-cancel">Annuler</button>
                        <button type="submit" disabled={loading} className="btn-submit">{loading ? 'Enregistrement...' : (contract ? 'Modifier' : 'Créer')}</button>
                    </div>
                </form>
            </div>

            <style>{`
          /* Copied styles from QuoteForm for consistency */
          .quote-form-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px; }
          .quote-form-container { background: var(--admin-panel); color: var(--admin-text); border-radius: 12px; width: 100%; max-width: 900px; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.35); border: 1px solid var(--admin-border); }
          .quote-form-header { display: flex; justify-content: space-between; align-items: center; padding: 24px 24px 0 24px; border-bottom: 1px solid var(--admin-border); margin-bottom: 24px; position: sticky; top: 0; background: var(--admin-panel); z-index: 10; }
          .quote-form-header h2 { margin: 0; font-size: 1.5rem; font-weight: 600; color: var(--admin-text); }
          .close-btn { background: none; border: 1px solid transparent; color: var(--admin-muted); cursor: pointer; padding: 8px; border-radius: 6px; }
          .close-btn:hover { background: rgba(255,255,255,0.06); border-color: var(--admin-border); color: var(--admin-text); }
          .quote-form { padding: 0 24px 24px 24px; }
          .form-section { margin-bottom: 32px; padding-bottom: 24px; border-bottom: 1px solid var(--admin-border); }
          .form-section h3 { margin: 0 0 20px 0; font-size: 1.2rem; font-weight: 600; color: var(--admin-text); border-left: 4px solid var(--admin-accent); padding-left: 12px; }
          .form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px; }
          .form-group { margin-bottom: 16px; }
          .form-group label { display: block; margin-bottom: 6px; font-weight: 500; }
          .form-group input, .form-group select, .form-group textarea { width: 100%; padding: 10px 12px; border: 1px solid var(--admin-border); background: var(--admin-panel-2); color: var(--admin-text); border-radius: 6px; box-sizing: border-box; }
          .form-actions { display: flex; gap: 12px; justify-content: flex-end; margin-top: 32px; padding-top: 24px; border-top: 1px solid var(--admin-border); position: sticky; bottom: 0; background: var(--admin-panel); }
          .btn-cancel, .btn-submit { padding: 10px 20px; border-radius: 8px; font-weight: 500; cursor: pointer; border: none; }
          .btn-cancel { background: var(--admin-panel-2); color: var(--admin-text); border: 1px solid var(--admin-border); }
          .btn-submit { background: var(--admin-accent); color: #fff; }
          .select-with-add { display: flex; gap: 8px; }
          .select-with-add select { flex: 1; }
          .add-btn { background: #10b981; color: #fff; border: none; border-radius: 6px; width: 42px; cursor: pointer; display: flex; align-items: center; justify-content: center; }
          .error-text { color: #ef4444; font-size: 0.8rem; margin-top: 4px; display: block; }
          .info-text { color: var(--admin-muted); font-size: 0.8rem; margin-top: 4px; }
       `}</style>

            {showQuickCustomerForm && (
                <QuickCustomerForm onSubmit={handleQuickCustomerSubmit} onCancel={() => setShowQuickCustomerForm(false)} loading={creatingCustomer} />
            )}
            {showQuickVehicleForm && (
                <QuickVehicleForm customerId={formData.customer_id} onSubmit={handleQuickVehicleSubmit} onCancel={() => setShowQuickVehicleForm(false)} loading={creatingVehicle} />
            )}
        </div>
    );
};

export default ContractForm;
